<template>
  <div>
    <b-dropdown
      :id="'services-dropdown-' + Id"
      :variant="'outline-' + globalVariant"
      size="sm"
      pill
      no-caret
      class="m-1 shadow-sm"
      toggle-class="d-inline-flex align-items-center font-weight-bold">
      <template #button-content>
        <font-awesome-icon icon="fa-solid fa-rss" class="mr-1"/>
        <span>{{ Services.length }} {{ $t('Services') }}</span>
        <b-icon icon="chevron-down" class="ml-1" font-scale="0.8" />
      </template>
      <b-dropdown-item-button
        v-for="(service, index) in Services"
        :key="index"
        :disabled="isExpired(service)"
        class="service-item">
        <div class="d-flex align-items-center">
          <b-icon 
            icon="circle-fill" 
            :variant="getServiceVariant(service)" 
            class="mr-2" 
            font-scale="0.7"/>
          <div :class="{'text-muted': isExpired(service)}">
            <div class="font-weight-bold d-flex justify-content-between">
              <span>{{ service.Name }}</span>
              <small class="ml-2">({{ $t(directionEnum[service.Direction]) }})</small>
            </div>
            <div class="small">
              {{ service.State ? '#' + $t(sMAServiceStateEnum[service.State]) : '' }}
            </div>
            <div class="small">
                {{ service.Type ? '#' +  $t(sMADataTypeEnum[service.Type]) : '' }}
            </div>
            <div class="small">
                {{ service.EndReason ? '#' + service.EndReason : '' }}
            </div>
            <div class="small italic text-info" v-if="service.EndDate">
              {{ $t('Until') }} : {{ formatDate(service.EndDate) }}
            </div>
          </div>
        </div>
      </b-dropdown-item-button>
      <b-dropdown-text v-if="Services.length === 0" class="text-muted small">
        {{ $t('NoService') }}
      </b-dropdown-text>
    </b-dropdown>
  </div>
</template>

<script lang="ts">
import { Vue, Component, Prop } from 'vue-property-decorator';
import { DirectionEnum, Service, SMADataTypeEnum, SMAServiceStateEnum } from '@/types/Administration/SMA/MetersInformations';
import locale from '../../locales/SMA.json';

@Component({
   i18n: { messages: locale }
})
export default class ServicesCell extends Vue {

    @Prop({ default: () => '' }) 
    public Id!: string;

    @Prop({ default: () => [] }) 
    public Services!: Service[];

    /**
     * Enum des statuts du service
     */
    public sMAServiceStateEnum = SMAServiceStateEnum;
    public directionEnum = DirectionEnum;
    public sMADataTypeEnum = SMADataTypeEnum;

    /**
     * Couleur globale du bouton déclencheur
     * Priorité : Rouge > Orange > Vert > Gris
     */
    get globalVariant(): string {
        if (this.Services.length === 0) return 'secondary';

        const variants = this.Services.map(s => this.getServiceVariant(s));

        if (variants.includes('danger')) return 'danger';
        if (variants.includes('warning')) return 'warning';
        if (variants.includes('success')) return 'success';
        return 'secondary';
    }

    /**
     * Logique de couleur par service
     */
    public getServiceVariant(service: Service): string {
        const now = new Date();
        const endDate = service.EndDate ? new Date(service.EndDate) : null;
        const state = this.$t(SMAServiceStateEnum[service.State]).toString()?.toUpperCase() || '';

        // 1. GRIS : Si expiré par la date
        if (endDate && endDate < now) {
            return 'secondary';
        }

        // 2. ROUGE : Statuts critiques ou révoqués
        const dangerStates = ['REFUSED', 'REVOKED', 'OBSOLETE', 'TERMINE', 'RÉFUSÉE', 'RÉVOQUÉE'];
        if (dangerStates.includes(state)) {
            return 'danger';
        }

        // 3. ORANGE : Proche de la fin (moins de 30 jours)
        if (endDate) {
            const oneMonthFromNow = new Date();
            oneMonthFromNow.setMonth(now.getMonth() + 1);
            if (endDate < oneMonthFromNow) {
                return 'warning';
            }
        }

        // 4. VERT : Actif et valide
        const successStates = ['ACTIV', 'ACTIF', 'ACTIVES', 'VALIDATED'];
        if (successStates.includes(state)) {
            return 'success';
        }

        return 'info'; // Par défaut
    }

    /** Vérifie si le service est expiré pour le griser */
    public isExpired(service: Service): boolean {
        if (!service.EndDate) return false;
        return new Date(service.EndDate) < new Date();
    }

    /** Formatage de date simple */
    public formatDate(date: Date): string {
        if (!date) return '-';
        return new Date(date).toLocaleDateString();
    }
}
</script>

<style scoped>
.service-item {
  min-width: 250px;
  border-bottom: 1px solid #f8f9fa;
}
.service-item:last-child {
  border-bottom: none;
}
.italic {
  font-style: italic;
}
</style>